// El modelo de enfermería con que se VALORA en el PAE (pedido del fundador,
// 2026-10-02: "debería ser opción de modelo de enfermería al realizar PAE
// rápido, y luego de la valoración en PAE detallado, en lugar de
// Henderson"). Dominio PURO: sin React, sin Supabase, sin IA.
//
// Qué cambia y qué no:
//  - La persona marca lo alterado con las categorías de SU modelo (patrones
//    de Gordon, actividades de Roper, estados de Astorquiza) y la valoración
//    viaja a la IA con ese nombre. El PROMPT del diagnóstico (diagnosisPrompt,
//    clavado en Sonnet 4.6) no cambia una letra: ya era agnóstico de modelo,
//    y el formato libre ya le mandaba otra forma de valoración.
//  - Con Henderson todo queda BYTE A BYTE como antes (fijado por test): mismas
//    claves (`rapido.necesidades`, `data.henderson`), mismas líneas, mismo
//    alcance clínico.
//  - La red post-IA (`parseDiagnosisResponse`) marca los diagnósticos que caen
//    fuera de lo marcado. Con Henderson usa su tabla curada de siempre
//    (HENDERSON_DIAGNOSIS_CODES_BY_NEED). Con los otros modelos usa el MISMO
//    mapa NANDA → sección de "Ponerlo en mi pauta" (`seccionParaDiagnostico`):
//    la valoración y la pauta hablan el mismo idioma, y no nace un tercer
//    catálogo.
//  - Entran los cuatro modelos con pauta en `paePauta.js` y Orem (pedido del
//    fundador, misma noche: "suma Orem también"). Orem no tiene pauta, así
//    que su alcance en la red post-IA sale de la tabla curada de Henderson a
//    través de su reparto (`alcance`), y "Ponerlo en mi pauta" no lo
//    preselecciona. Roy queda en la Enciclopedia hasta que la demanda lo
//    traiga.

import { HENDERSON_NEEDS, NANDA_DB } from '../data/nanda';
import { PAUTAS, seccionParaDiagnostico } from './paePauta';

// Catálogo de categorías por modelo. Los ids y nombres de Gordon, Roper y
// Astorquiza son los de las secciones de PAUTAS (fuente única, fijado por
// test). `pista` dice qué va ahí, corto, como `desc` en HENDERSON_NEEDS.
// `necesidades`: qué necesidades de Henderson vierten su evidencia en la
// categoría cuando el PAE Detallado la infiere desde los sistemas (cada
// necesidad cae en UNA sola categoría por modelo, fijado por test). Sigue al
// mapa por dominio de paePauta donde la necesidad tiene un dominio claro.
const CATEGORIAS = {
  gordon: [
    { id: 'g01', pista: 'Cómo percibe y cuida su salud: hábitos, adherencia, riesgos', necesidades: ['aprendizaje'] },
    { id: 'g02', pista: 'Ingesta, líquidos, glicemia, temperatura, piel y mucosas', necesidades: ['nutricion', 'temperatura', 'higiene', 'seguridad'] },
    { id: 'g03', pista: 'Eliminación intestinal, urinaria y por la piel', necesidades: ['eliminacion'] },
    { id: 'g04', pista: 'Respiración, circulación, movilidad, autocuidado y ocio', necesidades: ['respiracion', 'circulacion', 'movilidad', 'vestido', 'recreacion'] },
    { id: 'g05', pista: 'Cantidad y calidad del sueño y del descanso', necesidades: ['sueno'] },
    { id: 'g06', pista: 'Conciencia, orientación, sentidos, memoria y dolor', necesidades: ['comunicacion'] },
    { id: 'g07', pista: 'Cómo se ve a sí mismo, imagen corporal y emociones', necesidades: [] },
    { id: 'g08', pista: 'Familia, red de apoyo, roles y comunicación', necesidades: ['trabajorol'] },
    { id: 'g09', pista: 'Salud sexual y reproductiva', necesidades: [], alcance: ['comunicacion', 'trabajorol'] },
    { id: 'g10', pista: 'Cómo enfrenta la enfermedad y el estrés', necesidades: [] },
    { id: 'g11', pista: 'Lo que le da sentido, creencias y espiritualidad', necesidades: ['creencias'] },
  ],
  roper: [
    { id: 'r01', pista: 'Riesgos del entorno: caídas, infecciones, dispositivos', necesidades: ['seguridad'] },
    { id: 'r02', pista: 'Conciencia, sentidos, lenguaje, dolor y emociones', necesidades: ['comunicacion', 'aprendizaje'] },
    { id: 'r03', pista: 'Respiración y circulación', necesidades: ['respiracion', 'circulacion'] },
    { id: 'r04', pista: 'Ingesta de alimentos y líquidos, deglución', necesidades: ['nutricion'] },
    { id: 'r05', pista: 'Eliminación urinaria e intestinal', necesidades: ['eliminacion'] },
    { id: 'r06', pista: 'Higiene, piel, mucosas y vestirse', necesidades: ['higiene', 'vestido'] },
    { id: 'r07', pista: 'Temperatura corporal y cómo se regula', necesidades: ['temperatura'] },
    { id: 'r08', pista: 'Movilidad, postura y actividad', necesidades: ['movilidad'] },
    { id: 'r09', pista: 'Trabajo o estudio, recreación y roles', necesidades: ['trabajorol', 'recreacion'] },
    { id: 'r10', pista: 'Identidad, relaciones y salud sexual', necesidades: [], alcance: ['comunicacion', 'trabajorol'] },
    { id: 'r11', pista: 'Sueño y descanso', necesidades: ['sueno'] },
    { id: 'r12', pista: 'Cómo vive la idea de la muerte, el duelo o el final de la vida', necesidades: ['creencias'] },
  ],
  // Orem: los ocho requisitos universales con el vocabulario de su ficha en
  // la Enciclopedia (modelosEnfermeria.js), más los del desarrollo y los de
  // desviación de la salud. Sin pauta, los nombres viven aquí. `alcance` son
  // las necesidades de Henderson que cubre cada requisito para la red
  // post-IA (puede repetir; `necesidades`, el reparto de la evidencia, no).
  orem: [
    { id: 'o_aire', label: 'Aire', pista: 'Respiración, oxigenación y circulación', necesidades: ['respiracion', 'circulacion'] },
    { id: 'o_agua', label: 'Agua', pista: 'Hidratación, ingesta de líquidos y balance hídrico', necesidades: [], alcance: ['nutricion', 'eliminacion'] },
    { id: 'o_alimento', label: 'Alimento', pista: 'Ingesta, deglución, glicemia y estado nutricional', necesidades: ['nutricion'] },
    { id: 'o_eliminacion', label: 'Eliminación', pista: 'Eliminación urinaria e intestinal', necesidades: ['eliminacion'] },
    { id: 'o_actividad', label: 'Actividad y reposo', pista: 'Movilidad, sueño, descanso y autocuidado', necesidades: ['movilidad', 'sueno', 'vestido'], alcance: ['movilidad', 'sueno', 'vestido', 'higiene'] },
    { id: 'o_social', label: 'Soledad e interacción social', pista: 'Comunicación, compañía, red de apoyo y recreación', necesidades: ['trabajorol', 'recreacion'], alcance: ['comunicacion', 'trabajorol', 'recreacion'] },
    { id: 'o_peligros', label: 'Prevención de peligros', pista: 'Riesgos para la vida y el bienestar: caídas, infecciones, piel, conciencia, temperatura', necesidades: ['seguridad', 'higiene', 'temperatura', 'comunicacion'] },
    { id: 'o_normalidad', label: 'Normalidad', pista: 'Funcionamiento según sus capacidades, autoimagen y creencias', necesidades: ['creencias'], alcance: ['creencias', 'trabajorol', 'comunicacion'] },
    { id: 'o_desarrollo', label: 'Requisitos del desarrollo', pista: 'Lo propio de su etapa de vida y lo que la afecta', necesidades: [], alcance: ['trabajorol', 'aprendizaje', 'comunicacion'] },
    { id: 'o_desviacion', label: 'Desviación de la salud', pista: 'Lo que exige su enfermedad: tratamiento, adherencia y efectos', necesidades: ['aprendizaje'], alcance: ['aprendizaje', 'seguridad'] },
  ],
  // Las áreas de Astorquiza con los estados de necesidad que la ficha de la
  // Enciclopedia trae de Barrientos y Sánchez, 2024 (modelosEnfermeria.js).
  // Agrupar por área es una adaptación de MiPAE para el PAE (VOCABULARIO
  // lo dice en pantalla). El nombre de cada necesidad dentro del área lo pone
  // la pauta de cada universidad.
  astorquiza: [
    { id: 'a_bio', pista: 'Circulación, respiración, alimentación y digestión, termorregulación, regulación hidroelectrolítica, actividad y reposo, piel y mucosas, reproducción, anticuerpos', necesidades: ['respiracion', 'circulacion', 'nutricion', 'eliminacion', 'movilidad', 'sueno', 'vestido', 'temperatura', 'higiene', 'seguridad'] },
    { id: 'a_psi', pista: 'Conocimiento, afecto, autoestimación y egoísmo sano', necesidades: ['comunicacion', 'creencias', 'aprendizaje'] },
    { id: 'a_soc', pista: 'Estatus, poder y prestigio; afiliación; comunicación; recreación', necesidades: ['trabajorol', 'recreacion'] },
  ],
};

// Vocabulario por modelo: cómo se nombra la unidad y en qué género concuerda
// "alterada/alterado". `encabezadoIA` es la primera línea de la valoración
// que viaja a la IA; la de Henderson es la de siempre, letra por letra.
// `resumen` va al PDF y `etiqueta` a la pantalla de resumen; ninguna de las
// dos viaja a la IA. `portada` es el subtítulo de la portada del PDF (la de
// Henderson es la frase que la portada decía siempre).
const VOCABULARIO = {
  henderson: {
    corto: 'Henderson',
    titulo: 'Necesidades de Henderson',
    opcion: 'Por necesidades de Henderson',
    unidad: 'necesidad', unidades: 'necesidades', femenino: true,
    // Henderson describe 14 necesidades y el PAE muestra 15 filas porque
    // MiPAE separa respiración y circulación (HENDERSON_NEEDS). La ficha de la
    // Enciclopedia dice 14 y el PAE lo explica en lo que se ve, sin tocar lo
    // que viaja a la IA (auditoría del 4-oct-2026).
    resumen: 'Henderson, 14 necesidades (MiPAE separa respiración y circulación)',
    etiqueta: 'Henderson (14 necesidades; MiPAE separa respiración y circulación)',
    portada: 'Razonamiento clínico orientado por necesidades',
    encabezadoIA: 'Necesidades alteradas (Henderson):',
  },
  gordon: {
    corto: 'Gordon',
    titulo: 'Patrones funcionales de Gordon',
    opcion: 'Por patrones funcionales de Gordon',
    unidad: 'patrón', unidades: 'patrones', femenino: false,
    resumen: 'Gordon, 11 patrones funcionales',
    etiqueta: 'Gordon (11 patrones funcionales)',
    portada: 'Razonamiento clínico orientado por patrones funcionales',
    encabezadoIA: 'Patrones funcionales alterados (Gordon):',
  },
  roper: {
    corto: 'Roper',
    titulo: 'Actividades de la vida de Roper, Logan y Tierney',
    opcion: 'Por actividades de la vida (Roper)',
    unidad: 'actividad', unidades: 'actividades', femenino: true,
    resumen: 'Roper, Logan y Tierney, 12 actividades de la vida',
    etiqueta: 'Roper, Logan y Tierney (12 actividades de la vida)',
    portada: 'Razonamiento clínico orientado por actividades de la vida',
    encabezadoIA: 'Actividades de la vida alteradas (Roper, Logan y Tierney):',
  },
  // Orem y Astorquiza (revisión bibliográfica del 3-oct-2026): marcar una
  // categoría dice que ahí hay hallazgos, nada más. "Con déficit" afirmaba un
  // déficit de autocuidado que solo sale de comparar la demanda con la agencia,
  // e "insatisfecho" es una conclusión por estado en la que las fuentes de
  // Astorquiza no coinciden. "Comprometido" es la palabra que el formulario
  // ya usa para todos los modelos ("Marca los patrones comprometidos…").
  // Las dos listas son una AGRUPACIÓN de MiPAE para el PAE y `nota` lo dice
  // en pantalla. Henderson, Gordon y Roper no cambian.
  orem: {
    corto: 'Orem',
    titulo: 'Requisitos de autocuidado de Orem',
    opcion: 'Por requisitos de autocuidado (Orem)',
    unidad: 'requisito', unidades: 'requisitos', femenino: false,
    alterada: 'comprometido', alteradas: 'comprometidos',
    // Ocho universales uno por uno, y los del desarrollo y los de desviación
    // de la salud como dos grupos: no son "10 requisitos".
    resumen: 'Orem, requisitos de autocuidado agrupados por MiPAE',
    etiqueta: 'Orem (requisitos de autocuidado, agrupación de MiPAE)',
    portada: 'Razonamiento clínico orientado por requisitos de autocuidado',
    nota: 'Esta lista es una agrupación de MiPAE para el PAE. Trae los ocho requisitos universales uno por uno y, como dos grupos, los del desarrollo y los de desviación de la salud. Marcar un requisito no afirma un déficit de autocuidado; para eso compara lo que la persona necesita hacer con lo que hoy puede hacer.',
    encabezadoIA: 'Requisitos de autocuidado comprometidos (Orem):',
  },
  // Las tres áreas (biológica, psicológica y social) agrupan varios estados
  // de necesidad cada una: la unidad que se marca es el ÁREA, no un estado.
  astorquiza: {
    corto: 'Astorquiza',
    titulo: 'Áreas de los estados de necesidad de Astorquiza',
    opcion: 'Por estados de necesidad de Astorquiza',
    unidad: 'área', unidades: 'áreas', femenino: true,
    // "un área", "el área": femenino con artículo de a tónica.
    un: 'un', el: 'el',
    alterada: 'comprometida', alteradas: 'comprometidas',
    resumen: 'Astorquiza, estados de necesidad en 3 áreas agrupadas por MiPAE',
    etiqueta: 'Astorquiza (estados de necesidad en 3 áreas, agrupación de MiPAE)',
    portada: 'Razonamiento clínico orientado por estados de necesidad',
    nota: 'Las tres áreas son una agrupación de MiPAE para el PAE. En cada área, describe cada estado de necesidad con sus datos, separando lo alterado de cómo se está satisfaciendo. Cómo concluir cada estado depende de la pauta de tu asignatura.',
    encabezadoIA: 'Áreas de los estados de necesidad comprometidas (Astorquiza):',
  },
};

// En el orden en que se ofrecen: Henderson primero (el de siempre, y el que
// más usan las pautas), después por demanda medida en las preguntas a Benito.
export const MODELOS_VALORACION = Object.freeze(['henderson', 'gordon', 'roper', 'orem', 'astorquiza']);
export const MODELO_VALORACION_DEFAULT = 'henderson';

// Lo que el enlace "¿Cuál elijo?" deja escrito en el panel de Benito. Benito
// responde desde las fichas de la Enciclopedia (herramienta consultar_modelo).
export const PREFILL_ELEGIR_MODELO = '¿Qué modelo de enfermería me conviene para valorar en mi PAE: Henderson, Gordon, Roper, Orem o Astorquiza? ¿En qué se diferencian?';

export function esModeloValoracion(id) {
  return MODELOS_VALORACION.includes(id);
}

function categoriasDeModelo(id) {
  if (id === 'henderson') {
    return HENDERSON_NEEDS.map((n) => ({ id: n.id, label: n.label, pista: n.desc, necesidades: [n.id] }));
  }
  const secciones = PAUTAS[id]?.secciones || [];
  const nombre = new Map(secciones.map((s) => [s.id, s.nombre]));
  return (CATEGORIAS[id] || []).map((c) => ({ ...c, label: nombre.get(c.id) || c.label || c.id }));
}

const CACHE = new Map();

/**
 * El modelo con todo lo que la UI y la IA necesitan. Un id desconocido o
 * ausente cae a Henderson: un borrador viejo jamás queda sin valoración.
 */
export function modeloValoracion(id) {
  const clave = esModeloValoracion(id) ? id : MODELO_VALORACION_DEFAULT;
  if (!CACHE.has(clave)) {
    const v = VOCABULARIO[clave];
    CACHE.set(clave, Object.freeze({
      id: clave,
      ...v,
      alterada: v.alterada || (v.femenino ? 'alterada' : 'alterado'),
      alteradas: v.alteradas || (v.femenino ? 'alteradas' : 'alterados'),
      tienePauta: Boolean(PAUTAS[clave]),
      categorias: Object.freeze(categoriasDeModelo(clave)),
    }));
  }
  return CACHE.get(clave);
}

// "1 patrón alterado", "3 necesidades alteradas".
export function conteoAlteradas(modelo, n) {
  const m = modeloValoracion(modelo);
  return `${n} ${n === 1 ? m.unidad : m.unidades} ${n === 1 ? m.alterada : m.alteradas}`;
}

// La línea de la valoración en el PDF del Rápido: "Modelo de Gordon, 1 patrón
// alterado." La arman la app (pdfPayload) y el servidor (api/pdf/generate,
// para la app instalada vieja) con esta MISMA función. El "de" no es adorno:
// el servidor pasa el payload entero por el sanitizador de PII y "Modelo
// Gordon" (dos palabras seguidas con mayúscula) caía en su heurística de
// nombre propio, así que el PDF decía "[redactado], 1 patrón alterado." con
// el sello de datos sanitizados (auditoría del 4-oct-2026).
export function lineaValoracionPdf(modelo, n) {
  const m = modeloValoracion(modelo);
  return `Modelo de ${m.corto}, ${conteoAlteradas(m.id, n)}.`;
}

// ─── PAE Rápido ──────────────────────────────────────────────────────────────
//
// Dónde viven las marcas: Henderson en `rapido.necesidades` (la clave de
// siempre, así los borradores y PAE guardados se leen igual); cada otro
// modelo en `rapido.categorias[modelo]`, para que cambiar de modelo a mitad
// no mezcle marcas y volver al anterior las encuentre donde quedaron.

// El modelo de valoración del borrador, o null si está en formato libre.
export function modeloDelRapido(rapido = {}) {
  if (rapido.modelo === 'libre') return null;
  return modeloValoracion(rapido.modelo);
}

export function marcasRapido(rapido = {}) {
  const m = modeloDelRapido(rapido);
  if (!m) return {};
  if (m.id === 'henderson') return rapido.necesidades || {};
  return rapido.categorias?.[m.id] || {};
}

// La escritura que corresponde, como [clave, valor] para update('rapido', …).
export function escrituraMarcas(rapido = {}, marcas = {}) {
  const m = modeloDelRapido(rapido) || modeloValoracion(MODELO_VALORACION_DEFAULT);
  if (m.id === 'henderson') return ['necesidades', marcas];
  return ['categorias', { ...(rapido.categorias || {}), [m.id]: marcas }];
}

// Las categorías marcadas del modelo activo, en el orden del modelo.
export function alteradasRapido(rapido = {}) {
  const m = modeloDelRapido(rapido);
  if (!m) return [];
  const marcas = marcasRapido(rapido);
  return m.categorias.filter((c) => marcas[c.id]?.alterada);
}

// Las líneas de la valoración que viajan a la IA del diagnóstico (después de
// paciente, servicio, dx y motivo). Con Henderson, idénticas a las de antes.
export function lineasValoracionRapido(rapido = {}) {
  const m = modeloDelRapido(rapido);
  if (!m) return [];
  const marcas = marcasRapido(rapido);
  return [
    m.encabezadoIA,
    ...alteradasRapido(rapido).map((c) => `- ${c.label}: ${marcas[c.id]?.hallazgo || m.alterada}`),
  ];
}

// El texto de la valoración que el PAE Rápido manda a la IA del diagnóstico
// (después de "Valoración clínica:"). Fuente ÚNICA: lo usan RapidoStep3 y
// los casos clínicos por modelo (src/__tests__/clinical/modelos/), para que
// la prueba con la API real mida exactamente lo que manda la app. Con
// Henderson y formato libre es letra por letra el texto de siempre.
export function resumenParaDiagnosticoRapido(rapido = {}) {
  const d = rapido;
  if (d.modelo === 'libre') {
    const libre = d.libre || {};
    return [
      `Paciente: ${d.iniciales || '?'} ${d.edad || '?'}a ${d.sexo || ''}`,
      `Servicio: ${d.servicio || '?'}`,
      `Dx médico: ${d.dx_medico || '?'}`,
      `Motivo: ${d.motivo || '?'}`,
      `Lo que llama la atención: ${libre.llama_atencion || '?'}`,
      `Datos que lo confirman: ${libre.confirman || '?'}`,
      `A qué se debe: ${libre.se_debe || '?'}`,
      libre.que_mas ? `Otros problemas: ${libre.que_mas}` : null,
    ].filter(Boolean).join('\n');
  }
  return [
    `Paciente: ${d.iniciales || '?'} ${d.edad || '?'}a ${d.sexo || ''}`,
    `Servicio: ${d.servicio || '?'}`,
    `Dx médico: ${d.dx_medico || '?'}`,
    `Motivo: ${d.motivo || '?'}`,
    ...lineasValoracionRapido(d),
  ].join('\n');
}

// Hallazgos para la guarda de PII, con el mismo formato de `collectRapidoFields`
// (`necesidad.<id>` en Henderson, `categoria.<modelo>.<id>` en los otros).
// Revisa TODO hallazgo escrito, de cualquier modelo y también en formato
// libre, no solo el del modelo activo: antes se revisaban los de Henderson en
// cualquier modo, y la primera versión de esta capa dejó pasar un RUT escrito
// en Henderson con solo cambiar a formato libre (revisión clínica,
// 2026-10-02). Lo que la guarda bloquea no avanza, se use o no.
//
// El rótulo nombra el MODELO (auditoría del 4-oct-2026): el campo puede ser
// de un modelo que no está en pantalla, y "Hallazgo en respiración" no decía
// dónde buscarlo. `ubicarCampoPii` lee el id para que el aviso lleve al campo.
export function camposPiiRapido(rapido = {}) {
  const campos = [];
  for (const id of MODELOS_VALORACION) {
    const m = modeloValoracion(id);
    const marcas = id === 'henderson' ? (rapido.necesidades || {}) : (rapido.categorias?.[id] || {});
    for (const c of m.categorias) {
      if (!marcas[c.id]?.hallazgo) continue;
      campos.push({
        fieldId: id === 'henderson' ? `necesidad.${c.id}` : `categoria.${id}.${c.id}`,
        fieldLabel: `Hallazgo en ${c.label.toLowerCase()} (${m.corto})`,
        text: marcas[c.id].hallazgo,
      });
    }
  }
  return campos;
}

// De qué modelo y categoría es un campo de la guarda de PII, o null si no es
// un hallazgo de modelo (iniciales, formato libre…). Solo devuelve ids del
// catálogo: un id armado a mano no lleva a ninguna parte.
export function ubicarCampoPii(fieldId) {
  const partes = String(fieldId || '').split('.');
  let modelo = null;
  let categoria = null;
  if (partes[0] === 'necesidad' && partes.length === 2) [modelo, categoria] = ['henderson', partes[1]];
  else if (partes[0] === 'categoria' && partes.length === 3) [modelo, categoria] = [partes[1], partes[2]];
  if (!esModeloValoracion(modelo)) return null;
  return modeloValoracion(modelo).categorias.some((c) => c.id === categoria) ? { modelo, categoria } : null;
}

// ¿Hay alguna categoría marcada en CUALQUIER modelo? El primer hallazgo es
// uno por PAE: mirar solo el modelo activo lo volvía a emitir al cambiar de
// modelo y volver al paso 1 (auditoría del 4-oct-2026).
export function hayMarcasRapido(rapido = {}) {
  const conMarca = (marcas) => Object.values(marcas || {}).some((n) => n?.alterada);
  if (conMarca(rapido.necesidades)) return true;
  return Object.values(rapido.categorias || {}).some(conMarca);
}

// Huella de la valoración con que se armó el plan: el mismo texto que viaja
// a la IA del diagnóstico (`resumenParaDiagnosticoRapido`), resumido en un
// hash. Si la valoración cambia, el plan guardado ya no le corresponde
// (cambiar de Gordon a Roper dejaba el plan de Gordon con la valoración de
// Roper). No viaja a ninguna IA: es solo para comparar.
export function huellaValoracionRapido(rapido = {}) {
  const texto = resumenParaDiagnosticoRapido(rapido);
  let h = 5381;
  for (let i = 0; i < texto.length; i += 1) h = ((h * 33) ^ texto.charCodeAt(i)) >>> 0;
  return `v1-${h.toString(36)}-${texto.length}`;
}

// ─── Alcance clínico de la red post-IA ───────────────────────────────────────
//
// Lo que se le pasa a `parseDiagnosisResponse` para marcar los diagnósticos
// que caen fuera de lo que la persona marcó. Henderson: sus ids, como
// siempre. Otros modelos: los códigos NANDA cuya sección de pauta es una de
// las categorías marcadas (el mismo mapa con que "Ponerlo en mi pauta" los
// va a ubicar). Sin nada marcado, alcance vacío, igual que Henderson.
//
// Un modelo sin pauta (Orem) no tiene ese mapa: su alcance son las
// necesidades de Henderson que cubre cada requisito marcado (`alcance`), y
// la red usa la tabla curada de Henderson de siempre.
export function alcanceDiagnostico(modeloId, categoriasMarcadas = []) {
  const m = modeloValoracion(modeloId);
  const ids = [...new Set(categoriasMarcadas)];
  if (m.id === 'henderson') return { allowedNeedIds: ids, extraAllowedCodes: [] };
  if (!m.tienePauta) {
    const marcadas = new Set(ids);
    const necesidades = m.categorias
      .filter((c) => marcadas.has(c.id))
      .flatMap((c) => c.alcance || c.necesidades);
    return { allowedNeedIds: [...new Set(necesidades)], extraAllowedCodes: [] };
  }
  const marcadas = new Set(ids);
  const seccionDe = new Map(NANDA_DB.map((dx) => [dx.code, seccionParaDiagnostico(m.id, { codigo: dx.code, titulo: dx.label })]));
  const codigos = NANDA_DB.filter((dx) => marcadas.has(seccionDe.get(dx.code))).map((dx) => dx.code);
  // Una categoría que el mapa de la pauta deja SIN códigos ("Valores y
  // creencias", "Muerte", la sexualidad) no puede dejar el alcance vacío: en
  // el Detallado eso bloqueaba los diagnósticos sin aviso. Cae a la tabla
  // curada de Henderson por lo que cubre (revisión clínica, 2026-10-02).
  const conCodigo = new Set(seccionDe.values());
  const necesidades = m.categorias
    .filter((c) => marcadas.has(c.id) && !conCodigo.has(c.id))
    .flatMap((c) => c.alcance || c.necesidades);
  return { allowedNeedIds: [...new Set(necesidades)], extraAllowedCodes: codigos };
}

// ─── PAE Detallado ───────────────────────────────────────────────────────────
//
// El Detallado infiere desde los sistemas un mapa de Henderson
// (`henderson.js`, `data.henderson`) y eso NO cambia: es lo que sostiene la
// red clínica y, con Henderson, lo que viaja a la IA. Con otro modelo, la
// síntesis agrupa esa misma evidencia en las categorías del modelo y la
// persona marca las CATEGORÍAS (`data.sintesisModelo.estados`).

// Dos necesidades de Henderson del Detallado mezclan temas que los otros
// modelos separan: "seguridad" junta dispositivos e infección con delirium,
// pupilas, Glasgow y riesgo de caídas, y "comunicacion" junta el estado de
// conciencia con no poder hablar por el tubo. Verterlas enteras en una sola
// categoría ponía el delirium en "Nutricional y metabólico" de Gordon
// (revisión clínica, 2026-10-02). Estas rutas mandan cada evidencia a donde
// su modelo la valora, con el mismo criterio del mapa de "Ponerlo en mi
// pauta" (confusión y comunicación verbal a cognitivo-perceptual, caídas a
// actividad y ejercicio). Lo que no calza sigue el reparto por necesidad.
const RUTAS_EVIDENCIA = [
  {
    necesidades: ['seguridad', 'comunicacion'],
    re: /^(CAM-ICU|Pupilas|Glasgow|Conciencia|Orientación|RASS)\b/,
    destino: { gordon: 'g06', roper: 'r02', astorquiza: 'a_psi', orem: 'o_peligros' },
  },
  {
    necesidades: ['seguridad'],
    re: /^Downton\b/,
    destino: { gordon: 'g04', roper: 'r01', astorquiza: 'a_bio', orem: 'o_peligros' },
  },
  {
    necesidades: ['comunicacion'],
    re: /comunicación verbal imposible/,
    destino: { gordon: 'g06', roper: 'r02', astorquiza: 'a_soc', orem: 'o_social' },
  },
];

function categoriaDeEvidencia(m, necesidadId, texto) {
  if (m.id === 'henderson') return necesidadId;
  for (const r of RUTAS_EVIDENCIA) {
    if (r.necesidades.includes(necesidadId) && r.re.test(texto) && r.destino[m.id]) return r.destino[m.id];
  }
  return m.categorias.find((c) => c.necesidades.includes(necesidadId))?.id || null;
}

export function modeloDelDetallado(data = {}) {
  return modeloValoracion(data.sintesisModelo?.modelo);
}

/**
 * Estado por categoría: { [catId]: { estado, evidencias, inferida } }.
 * `inferido` es el mapa de Henderson deducido solo de los datos; `estados`
 * lo que la persona marcó (manda sobre lo inferido, igual que en Henderson).
 */
export function sintesisPorModelo(modeloId, inferido = {}, estados = {}) {
  const m = modeloValoracion(modeloId);
  const evidencias = Object.fromEntries(m.categorias.map((c) => [c.id, []]));
  const inferidas = new Set();
  for (const need of HENDERSON_NEEDS) {
    const st = inferido[need.id];
    if (!st) continue;
    for (const ev of st.evidencias || []) {
      const cat = categoriaDeEvidencia(m, need.id, ev);
      if (!cat || !evidencias[cat]) continue;
      evidencias[cat].push(ev);
      if (st.estado === 'alterada') inferidas.add(cat);
    }
  }
  const out = {};
  for (const c of m.categorias) {
    const inferida = inferidas.has(c.id);
    const marcado = estados[c.id];
    const estado = marcado === 'alterada' || marcado === 'sin_evaluar' ? marcado : (inferida ? 'alterada' : 'sin_evaluar');
    out[c.id] = { estado, evidencias: [...new Set(evidencias[c.id])], inferida };
  }
  return out;
}

// Ids de las categorías alteradas del Detallado, en el orden del modelo.
export function categoriasAlteradasDetallado(data = {}, inferido = {}) {
  const m = modeloDelDetallado(data);
  if (m.id === 'henderson') {
    return Object.entries(data.henderson || {})
      .filter(([, v]) => v?.estado === 'alterada')
      .map(([id]) => id);
  }
  const sintesis = sintesisPorModelo(m.id, inferido, data.sintesisModelo?.estados || {});
  return m.categorias.filter((c) => sintesis[c.id].estado === 'alterada').map((c) => c.id);
}

// Bloque del resumen que lee la IA del Detallado (el de Henderson vive en
// buildSummary y no se toca). Mismo formato que el de Henderson: un título,
// una línea por categoría, el estado en mayúsculas y la evidencia después.
export function bloqueSintesisModelo(modeloId, sintesis = {}) {
  const m = modeloValoracion(modeloId);
  const lineas = [`═══ ${m.titulo.toUpperCase()} ${m.alteradas.toUpperCase()} ═══`];
  for (const c of m.categorias) {
    const s = sintesis[c.id] || { estado: 'sin_evaluar', evidencias: [] };
    const estado = s.estado === 'alterada' ? m.alterada.toUpperCase() : 'SIN EVALUAR';
    const suffix = s.evidencias?.length ? `: ${s.evidencias.join(', ')}` : '';
    lineas.push(`- ${c.label}: ${estado}${suffix}`);
  }
  return lineas;
}

// La pauta con que "Ponerlo en mi pauta" parte para un modelo elegido, o null
// si no corresponde preseleccionar (Henderson deja actuar la memoria de
// ramos, como antes; Orem no tiene pauta).
export function pautaDelModelo(modeloId) {
  const m = modeloValoracion(modeloId);
  return m.id !== 'henderson' && m.tienePauta && esModeloValoracion(modeloId) ? m.id : null;
}

// ─── Preferencia del dispositivo ─────────────────────────────────────────────
//
// El último modelo con que valoró, para no volver a preguntar en cada PAE.
// Preferencia de dispositivo y nada más (patrón de `paeArranque`): si el
// storage no responde, manda Henderson.
const CLAVE_PREFERENCIA = 'mipae:pae:modelo';

function storagePorDefecto() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

// El modelo que la persona eligió en un PAE anterior en este dispositivo, o
// null si nunca eligió (o el storage no responde). Distinto de
// `modeloPreferido`, que cae a Henderson: el PAE Rápido necesita saber si hay
// una elección de verdad para abrir con ese modelo ya marcado.
export function modeloRecordado(storage = storagePorDefecto()) {
  try {
    const v = storage?.getItem(CLAVE_PREFERENCIA);
    return esModeloValoracion(v) ? v : null;
  } catch {
    return null;
  }
}

export function modeloPreferido(storage = storagePorDefecto()) {
  return modeloRecordado(storage) || MODELO_VALORACION_DEFAULT;
}

export function recordarModelo(id, storage = storagePorDefecto()) {
  if (!esModeloValoracion(id)) return false;
  try {
    if (!storage) return false;
    storage.setItem(CLAVE_PREFERENCIA, id);
    return true;
  } catch {
    return false;
  }
}
